import { FC, PropsWithChildren, useCallback, useRef, useState } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { MINIMUM_SEARCH_TERM_LENGTH } from '../../../config';
import { debounce } from '@bts-web/utils-functions';
import { ButtonBase } from '../../../common';
import { useFATCAFormState } from '../../utils/useFATCAFormState';
import { FATCATranslations } from '../../utils/getFATCATranslations';
import { BorderedTextInput } from '../reusable/FATCAInputs/BorderedTextInput/BorderedTextInput';

const InputWrapper: FC<PropsWithChildren> = ({ children }) => (
  <div
    className={css({
      display: 'flex',
      flexDirection: 'column',
      gap: 'extra_small_2',
      textAlign: 'start',
    })}
  >
    {children}
  </div>
);

const ConfirmDetailsScreen: FC<{
  fatcaFormState: ReturnType<typeof useFATCAFormState>;
  translations: FATCATranslations;
}> = ({ fatcaFormState, translations }) => {
  const {
    fatcaUserData,
    onNextStep,
    loading: fatcaStateLoading,
  } = fatcaFormState;

  const [_suggestions, setSuggestions] = useState<
    { label: string; value: string }[]
  >([]);

  const [addressSearchValue, setAddressSearchValue] = useState<string>(
    fatcaFormState.fatcaUserData?.address
      ? Object.values(fatcaFormState.fatcaUserData.address).join(' ')
      : '',
  );

  const [loading, setLoading] = useState(false);

  const debouncedSearchRef = useRef(
    debounce(async (input: string) => {
      const sanitizedInput = input.trim();

      if (
        sanitizedInput.length === 0 ||
        sanitizedInput.length < MINIMUM_SEARCH_TERM_LENGTH
      ) {
        setSuggestions([
          {
            label: 'suggestion street 3',
            value: 'suggestion_street_3',
          },
          {
            label: 'suggestion street 4',
            value: 'suggestion_street_4',
          },
          {
            label: 'suggestion street 5',
            value: 'suggestion_street_5',
          },
        ]);

        return;
      }

      try {
        console.info('Fetching suggestions for:', sanitizedInput);

        setSuggestions([]);
      } catch (error) {
        console.error('Error fetching assets:', error);
      } finally {
        setLoading(false);
      }
    }, 500),
  );

  const onInputChange = useCallback((newValue: string) => {
    setAddressSearchValue(newValue);

    setLoading(true);

    debouncedSearchRef.current(newValue);
  }, []);

  // const clearSearchParams = useCallback(() => {
  //   setAddressSearchValue('');

  //   setLoading(false);

  //   debouncedSearchRef.current('');
  // }, []);

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: 'small',
      })}
    >
      <InputWrapper>
        <label htmlFor="firstname">First Name:</label>
        <BorderedTextInput
          id="firstname"
          disabled
          value={fatcaUserData?.firstName ?? ''}
        />
      </InputWrapper>
      <InputWrapper>
        <label htmlFor="lastname">Last Name:</label>
        <BorderedTextInput
          id="lastname"
          disabled
          value={fatcaUserData?.lastName ?? ''}
        />
      </InputWrapper>
      <InputWrapper>
        <label htmlFor="dateOfBirth">Date of birth:</label>
        <BorderedTextInput
          id="dateOfBirth"
          disabled
          value={fatcaUserData?.dateOfBirth ?? ''}
        />
      </InputWrapper>
      <InputWrapper>
        <label htmlFor="address">Address:</label>
        <BorderedTextInput
          id="address"
          onChange={(e) => onInputChange(e.target.value)}
          defaultValue={
            fatcaUserData?.address
              ? Object.values(fatcaUserData.address).join(' ')
              : ''
          }
        />
      </InputWrapper>
      <ButtonBase
        visual="primary"
        onClick={onNextStep}
        disabled={loading || !addressSearchValue || fatcaStateLoading}
        fullWidth
        size="large"
      >
        {translations.continue}
      </ButtonBase>
    </div>
  );
};

export { ConfirmDetailsScreen };
